import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { gql, useMutation } from '@apollo/client';
import { Alert, Logo, TextArea, TextInput, Typography } from '@guildeducationinc/recess';
import { useAuth } from '@src/hooks/useAuth';
import { useCurrentClient } from '@src/hooks/useCurrentClient';
import { TrackableButton, useLocale } from 'pe-core-frontend';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from '../LoginForm/LoginForm.module.scss';
const SEND_SUPPORT_REQUEST = gql `
  mutation SEND_SUPPORT_REQUEST($input: SupportRequestInput!) {
    sendSupportRequest(input: $input) {
      status
      message
      response
    }
  }
`;
export const SupportRequest = () => {
    const { t } = useLocale();
    const { currentUser } = useAuth();
    const { currentClient } = useCurrentClient();
    const [email, setEmail] = useState('');
    const [fullName, setFullName] = useState('');
    const [uniqueId, setUniqueId] = useState('');
    const [preferredLang] = useState('ENGLISH');
    const [requestText, setRequestText] = useState('');
    const [error, setError] = useState('');
    const [errorField, setErrorField] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [sendSupportRequest] = useMutation(SEND_SUPPORT_REQUEST);
    const SUCCESS_MESSAGE = 'Thank you for reaching out. Confirmation with your ticket number has been sent to {email}.';
    // TODO: LANGUAGES will be used with Preferred Language select in the future
    // const LANGUAGES = [
    //   { label: 'English', value: 'ENGLISH' },
    //   { label: 'Spanish', value: 'SPANISH' },
    //   { label: 'French', value: 'FRENCH' },
    // ];
    useEffect(() => {
        if (currentUser) {
            setEmail(currentUser.email || '');
            setFullName(`${currentUser.firstName} ${currentUser.lastName}`.trim());
            setUniqueId(currentUser.uniqueId || '');
        }
    }, [currentUser]);
    const validateField = (fieldLabel, fieldName, value) => {
        let isValid = true;
        if (typeof value === 'number' && value === 0) {
            isValid = false;
        }
        else if (!value || String(value).trim() === '') {
            isValid = false;
        }
        if (!isValid) {
            setError(t(`${fieldName} is required`));
            setErrorField(fieldLabel);
        }
        return isValid;
    };
    const validate = () => {
        if (!validateField('email', 'Email', email))
            return false;
        if (!validateField('fname', 'First & Last name', fullName))
            return false;
        if (!validateField('empid', 'Employee ID', uniqueId))
            return false;
        if (!validateField('lang', 'Preferred language', preferredLang))
            return false;
        if (!validateField('request', 'Support request details', requestText))
            return false;
        return true;
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!validate())
            return;
        try {
            setSubmitting(true);
            const requestInput = {
                userId: currentUser?.id,
                email,
                clientId: currentClient?.id,
                employeeId: uniqueId,
                fullName,
                preferredLanguage: preferredLang,
                requestText,
            };
            // console.log('submitting support request:', requestInput);
            const { data } = await sendSupportRequest({
                variables: {
                    input: requestInput,
                },
            });
            // console.log('sendSupportRequest data:', data);
            if (data.sendSupportRequest.status === 'success') {
                setSubmitted(true);
            }
            else {
                setError(t('There was an error submitting your request. Please try again later.'));
            }
        }
        catch (e) {
            // don't display backend error to end user
            // setError((e as any).message);
            setError(t('There was an error submitting your request. Please try again later.'));
            setSubmitted(false);
        }
        finally {
            setSubmitting(false);
        }
    };
    return (_jsx("div", { className: styles.loginForm, children: _jsxs("div", { className: styles.container, children: [_jsx("div", { className: styles.clientHeader, children: _jsx(Link, { to: '/', children: _jsx(Logo, { fullLogo: true, alt: '' }) }) }), _jsxs("div", { className: styles.form, children: [submitted && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: 'h1', children: t('Request received!') }), _jsx(Typography, { variant: 'body1', children: t(SUCCESS_MESSAGE, { values: { email } }) }), _jsx(Typography, { variant: 'body1', children: t('A specialist will respond via email within the next 1-3 business days.') }), _jsx(Typography, { variant: 'label2', children: t('You can close this tab or create another request.') }), _jsx("div", { className: 'flex justify-end', children: _jsx(TrackableButton, { enText: 'New request', tag: 'cta', type: 'submit', onClick: () => window.location.reload(), children: t('New request') }) })] })), !submitted && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: 'h1', children: t('Contact support') }), _jsx(Typography, { variant: 'body1', gutterBottom: 'md', children: t('If you have questions or need assistance, our support team is ready to help. ' +
                                        "Reach out, and we'll get you back on track.") })] })), _jsx(Alert, { hideClose: true, display: !!error, type: 'danger', className: 'margin--bottom_24', children: error })] }), !submitted && (_jsx("fieldset", { disabled: submitting, children: _jsxs("form", { className: styles.form, onSubmit: handleSubmit, children: [_jsx(TextInput, { label: t('Employee email'), disabled: submitting || !!currentUser, required: true, className: 'margin--auto', maxLength: 80, value: email, type: 'email', onChange: e => setEmail(e.target.value), isInvalid: errorField === 'email' }), _jsx(TextInput, { label: t('First & Last name'), disabled: submitting || !!currentUser, required: true, className: 'margin--auto', maxLength: 255, value: fullName, onChange: e => setFullName(e.target.value), isInvalid: errorField === 'fname' }), _jsx(TextInput, { label: t('Employee ID'), disabled: submitting || !!currentUser, required: true, className: 'margin--auto', maxLength: 255, value: uniqueId, onChange: e => setUniqueId(e.target.value), isInvalid: errorField === 'empid' }), _jsx(TextArea, { label: t('Please share your question or concern, providing all relevant details. This will enable us to offer ' +
                                    "you the most accurate and prompt support possible. We're here to assist you!"), required: true, maxLength: 32000, fullWidth: true, value: requestText, onChange: e => setRequestText(e.target.value), isInvalid: errorField === 'request' }), _jsx("div", { children: _jsx("div", { className: 'flex justify-end', children: _jsx(TrackableButton, { enText: 'Submit', tag: 'form', type: 'submit', disabled: submitting, children: t('Submit') }, 'submit') }) })] }) }))] }) }));
};
